import { useMemo, forwardRef, useImperativeHandle } from "react"
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { PieChart } from "lucide-react"
import { DepartmentComparison } from "@/types"
import { useAuth } from "@/contexts/auth"
import { UserRole as UserRoleEnum } from "@/types/enums"
import ExportButton from "@/components/ui/ExportButton"
import DonutChart, { DonutChartData } from "@/components/ui/DonutChart"

interface DepartmentComparisonChartProps {
  data: DepartmentComparison[]
  title?: string
  isLoading?: boolean
  standalone?: boolean // When true, renders without Card container
  hasActiveFilters?: boolean // Whether filters are active
}

interface DepartmentComparisonChartHandle {
  download: () => void;
}

const DepartmentComparisonChart = forwardRef<DepartmentComparisonChartHandle, DepartmentComparisonChartProps>(
  function DepartmentComparisonChart(
      { 
    data, 
    title = "Overtime by Department",
    isLoading = false,
    standalone = false,
    hasActiveFilters = false
  },
    ref
  ) {
  const { user } = useAuth();

  // Filter and process data based on user role for department hours only
  const chartData = useMemo(() => {
    if (!data?.length) return [];

    // Filter departments based on user role
    let filteredData = data;
    if (user?.role === UserRoleEnum.DEPARTMENT_HEAD && user.department && Array.isArray(user.department)) {
      filteredData = data.filter(dept => user.department.includes(dept.department));
    }

    // Return department totals sorted by hours (descending)
    return filteredData
      .filter(dept => dept.hours > 0) // Only include departments with positive overtime hours
      .sort((a, b) => b.hours - a.hours)
      .map(dept => ({
        name: dept.department,
        hours: dept.hours
      }));
  }, [data, user]);

  // Convert data for DonutChart component
  const donutChartData = useMemo<DonutChartData[]>(() => {
    return chartData.map((item, index) => ({
      name: item.name,
      value: item.hours,
      fill: `hsl(var(--chart-${(index % 20) + 1}))`
    }));
  }, [chartData]);

  // Create chart config for DonutChart
  const donutChartConfig = useMemo(() => {
    const config: Record<string, { label: string; color?: string }> = {
      value: {
        label: "Hours",
      },
    };
    
    chartData.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: `hsl(var(--chart-${(index % 20) + 1}))`,
      };
    });
    
    return config;
  }, [chartData]);

  // Handle CSV download
  const handleDownload = () => {
    if (!chartData.length) return;

    const headers = ["Department", "Hours"];
    const csvData = chartData.map(dept => [
      dept.name,
      dept.hours.toFixed(1)
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `department_hours_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Chart content that will be used in both standalone and card modes
  const chartContent = (
    <div className="h-72">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse h-40 w-full bg-muted rounded-lg" />
        </div>
      ) : (
        <DonutChart
          data={donutChartData}
          chartConfig={donutChartConfig}
          isLoading={isLoading}
          showLegend={true}
          showCenterLabel={true}
          centerLabelValue={donutChartData.reduce((acc, curr) => acc + curr.value, 0).toFixed(0)}
          centerLabelSubtext="Total Hours"
          formatValue={(value) => `${value.toFixed(1)}h`}
          height={288}
          className="flex items-center justify-center"
        />
      )}
    </div>
  );
    
  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  // Render with or without Card container based on standalone prop
  if (standalone) {
    return chartContent;
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-3 px-6">
        <CardTitle className="text-lg font-semibold text-left flex items-center">
          <PieChart className="w-4 h-4 mr-2 text-primary" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton 
            onClick={handleDownload} 
            disabled={!chartData.length || isLoading} 
          />
        </div>
      </CardHeader>
      <CardContent className="pt-2 pb-4 flex-1 flex flex-col">
        <div className="h-72 flex-1">
          {donutChartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <PieChart className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {hasActiveFilters ? "No data matches your filters" : "No department data available"}
              </p>
              <p className="text-sm max-w-xs">
                {hasActiveFilters 
                  ? "Try adjusting the filters to see data"
                  : "Data will appear here once overtime requests are approved"
                }
              </p>
            </div>
          ) : (
            <DonutChart
              data={donutChartData}
              chartConfig={donutChartConfig}
              isLoading={isLoading}
              showLegend={true}
              showCenterLabel={true}
              centerLabelValue={donutChartData.reduce((acc, curr) => acc + curr.value, 0).toFixed(0)}
              centerLabelSubtext="Total Hours"
              formatValue={(value) => `${value.toFixed(1)}h`}
              height={288}
              innerRadius={80}
              outerRadius={120}
              className="flex items-center justify-center"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default DepartmentComparisonChart;
